import { resolveAndApplySpeakerForCompositionElement } from '@src/utility/compositionSettings';
import { makeAutoObservable, runInAction } from 'mobx';

import FadeProducer from '../fadeEffectProcessor/FadeProducer';
import { KARAOKE_TRACK_NUMBER } from './VideoCreatorStore';

import type { ElementState } from '../renderer/ElementState';
import type VideoCreatorStore from './VideoCreatorStore';
import type { RootStore } from '@src/stores-v2';

export default class TimelineStore {
  private videoCreator: VideoCreatorStore;
  private rootStore: RootStore;

  overrideTimelineElementsState: Record<string, Partial<ElementState>> = {};
  framerate: number = 24; //default legacy framerate

  private _shouldCalcTimelineScale = false;

  constructor(videoCreator: VideoCreatorStore, rootStore: RootStore) {
    this.videoCreator = videoCreator;
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetElementsStateOverrides() {
    this.overrideTimelineElementsState = {};
  }

  applyPlacementOffsetToActiveElements(offset: number) {
    const { activeElementIds } = this.videoCreator;
    const { state } = this.videoCreator;
    if (!state || !activeElementIds.length) {
      return;
    }
    this.applyPlacementOffsetToElements(
      state.elements.filter(element =>
        activeElementIds.includes(element.source.id),
      ),
      offset,
    );
  }

  private static ifElementsOverlapsWith(
    elements: ElementState[],
    newPlacement: ElementState,
    otherElements: ElementState[],
  ) {
    return otherElements.some(elOther => {
      if (elements.find(el => el.source.id === elOther.source.id)) {
        return false;
      }
      const elOtherEnd = elOther.globalTime + elOther.duration;
      const newPlacementEnd = newPlacement.globalTime + newPlacement.duration;
      // allow overlap at the edges
      return (
        Math.max(elOther.globalTime, newPlacement.globalTime) <
        Math.min(elOtherEnd, newPlacementEnd)
      );
    });
  }

  applyPlacementOverrideToElement(
    elementId: string,
    placement: Partial<
      Pick<ElementState, 'duration' | 'globalTime' | 'trimStart'>
    >,
  ) {
    runInAction(() => {
      this.overrideTimelineElementsState[elementId] = placement;
    });
  }

  async applyPlacements() {
    const allElements = this.videoCreator.state?.elements || [];
    const overridenElementIds = Object.keys(this.overrideTimelineElementsState);
    const selectedElements = allElements.filter(el =>
      overridenElementIds.includes(el.source.id),
    );
    if (overridenElementIds.length === 0 || selectedElements.length === 0)
      return;

    const currentPlayheadTime = this.videoCreator.time;
    let mainTrackElementsSelected = [];

    if (overridenElementIds.length > 1) {
      mainTrackElementsSelected = selectedElements.filter(el =>
        this.videoCreator.isOriginalVideoElement(el.source),
      );
    } else {
      mainTrackElementsSelected = [
        selectedElements.find(
          el =>
            !this.videoCreator.isImageElement(el) &&
            !(
              el.source.type === 'video' &&
              !this.videoCreator.isOriginalVideoElement(el.source)
            ),
        ),
      ].filter(Boolean) as ElementState[];
    }

    const firstElement = mainTrackElementsSelected[0] ?? selectedElements[0];
    const newPlacement = {
      ...firstElement,
      ...this.overrideTimelineElementsState[firstElement.source.id],
    };

    // only for moving elements
    // TODO handle move and trim cases separately
    if (
      mainTrackElementsSelected.length > 0 &&
      firstElement.duration === newPlacement.duration
    ) {
      const lastElement =
        mainTrackElementsSelected[mainTrackElementsSelected.length - 1];
      if (mainTrackElementsSelected.length > 1) {
        newPlacement.duration =
          lastElement.globalTime +
          lastElement.duration -
          firstElement.globalTime;
      }
      const overlapsWith = TimelineStore.ifElementsOverlapsWith(
        mainTrackElementsSelected,
        newPlacement,
        allElements.filter(el => el.track === firstElement.track),
      );

      if (overlapsWith || mainTrackElementsSelected.length > 1) {
        const nextElement = allElements.find(
          el =>
            el.track === lastElement.track &&
            el.globalTime > lastElement.globalTime,
        );
        // @ts-ignore
        const prevElement = allElements.findLast(
          (el: ElementState) =>
            el.track === firstElement.track &&
            el.globalTime < firstElement.globalTime,
        );

        if (
          (prevElement?.globalTime || 0) + (prevElement?.duration || 0) >
          newPlacement.globalTime
        ) {
          newPlacement.globalTime = this.videoCreator.snapTime(
            (prevElement?.globalTime || 0) + (prevElement?.duration || 0),
          );
        } else if (
          nextElement &&
          nextElement.globalTime <
            newPlacement.globalTime + newPlacement.duration
        ) {
          newPlacement.globalTime = this.videoCreator.snapTime(
            Math.max(nextElement.globalTime - newPlacement.duration, 0),
          );
        }
      }

      this.applyPlacementOffsetToActiveElements(
        newPlacement.globalTime - firstElement.globalTime,
      );
    }

    const leftmostElementTime = selectedElements.reduce((acc, el) => {
      const elTime =
        this.overrideTimelineElementsState[el.source.id].globalTime ??
        el.globalTime;
      return elTime < acc ? elTime : acc;
    }, Infinity);
    if (leftmostElementTime < 0) {
      newPlacement.globalTime -= leftmostElementTime;
      this.applyPlacementOffsetToElements(
        selectedElements,
        newPlacement.globalTime - firstElement.globalTime,
      );
    }

    if (overridenElementIds.length > 1) {
      await this.shiftElements(
        overridenElementIds,
        newPlacement.globalTime - firstElement.globalTime,
      );
      const fadeProducer = new FadeProducer(this.videoCreator);
      await fadeProducer.tidyOriginalVideoOverlay();
    } else {
      await this.updateElementPlacement(firstElement, newPlacement);
    }

    // Restore original playhead position if extending timeline
    const isExtendingTimeline = selectedElements.some(el => {
      const override = this.overrideTimelineElementsState[el.source.id];
      const newDuration = override.duration || el.duration;
      const newGlobalTime = override.globalTime ?? el.globalTime;
      const newElementEndTime = newGlobalTime + newDuration;
      return (
        newDuration > el.duration &&
        newElementEndTime > this.videoCreator.duration
      );
    });
    if (isExtendingTimeline) {
      setTimeout(() => this.videoCreator.setTime(currentPlayheadTime, true));
    }

    // Rest on timeout since weird render chain breaking stuff
    // happens when we apply placement and then immediately reset the state
    // Update: the issue is that Timeline.tsx uses videoCreatorStore.tracks which
    // get updated later, after the source refresh timeout
    this.videoCreator.rootStore.videoSourceStore.onNextSourceRefresh(() => {
      this.resetElementsStateOverrides();
    });
  }

  // offset - number of seconds to shift the elements by
  applyPlacementOffsetToElements(elements: ElementState[], offset: number) {
    runInAction(() => {
      elements.forEach(element => {
        this.overrideTimelineElementsState[element.source.id] = {
          globalTime: element.globalTime + offset,
        };
      });
    });
  }

  get shouldCalcTimelineScale() {
    return this._shouldCalcTimelineScale;
  }

  setShouldCalcTimelineScale(value: boolean) {
    this._shouldCalcTimelineScale = value;
  }

  async shiftElements(elementIds: string[], timeShift: number) {
    const { headlessEnabled, editor, useTranscriptSegments, useNewKaraoke } =
      this.rootStore.editorStore;
    let editSource = true;
    if (headlessEnabled && editor) {
      editSource = false;
      editor.commandShiftMultipleElements(elementIds, timeShift);
    } else {
      console.error(
        'shiftElements is not supported without headless editor enabled',
      );
      return;
    }
    if (!useTranscriptSegments) {
      await this.videoCreator.videoTranscriptionProcessor.moveElements(
        elementIds,
        timeShift,
        editSource,
      );
    }

    if (!useNewKaraoke) {
      this.videoCreator.refreshKaraokeElements();
    }
  }

  async updateElementPlacement(
    element: ElementState,
    placement: Pick<ElementState, 'duration' | 'globalTime' | 'trimStart'>,
  ) {
    if (
      element.globalTime === placement.globalTime &&
      element.duration === placement.duration
    )
      return;

    const fadeProducer = new FadeProducer(this.videoCreator, element);

    if (
      this.videoCreator.isOriginalVideoElement(element.source) &&
      this.rootStore.transcriptionStore.getFinalTranscriptionElements()?.length
    ) {
      if (!this.rootStore.editorStore.useTranscriptSegments) {
        this.videoCreator.saveVideoStateSnapshot(
          undefined,
          `moving ${element.source.type} element`,
        );
      }
      await this.legacy_updateMainVideoElementPlacementAndTranscript(
        element,
        placement,
        fadeProducer,
      );
      return;
    }

    if (element.track === KARAOKE_TRACK_NUMBER) {
      if (this.rootStore.editorStore.useNewKaraoke) {
        this.updateKaraokeElementPlacement(element, placement);
      } else {
        this.videoCreator.saveVideoStateSnapshot(
          undefined,
          `moving ${element.source.type} element`,
        );
        await this.legacy_updateKaraokeElementPlacement(element, placement);
      }
      return;
    }

    if (this.rootStore.editorStore.headlessEnabled) {
      // Use headless editor commands for all elements (audio, image, text, etc.)
      this.updateMediaElementPlacement(element, placement, fadeProducer);
      if (element.source.type === 'composition') {
        await resolveAndApplySpeakerForCompositionElement({
          elementId: element.source.id,
          editorStore: this.videoCreator.rootStore.editorStore as any,
          datoClientStore: this.rootStore.datoClientStore,
          videoCreator: this.videoCreator,
          timelineTime: placement.globalTime,
        });
      }
    } else {
      this.videoCreator.saveVideoStateSnapshot(
        undefined,
        `moving ${element.source.type} element`,
      );
      this.legacy_updateMediaElementPlacement(element, placement, fadeProducer);
    }
  }

  private updateKaraokeElementPlacement(
    element: ElementState,
    placement: Pick<ElementState, 'duration' | 'globalTime' | 'trimStart'>,
  ) {
    // TODO: handle subtitles also
    if (!this.rootStore.editorStore.editor) return;
    const karaokeLine = this.rootStore.karaokeStore.getKaraokeLineById(
      element.source.id,
    );
    if (!karaokeLine) {
      console.error('Karaoke line not found for element', element.source.id);
      return;
    }
    const startOffset = placement.globalTime - karaokeLine.transcriptStartTime;
    const endOffset =
      placement.globalTime + placement.duration - karaokeLine.transcriptEndTime;

    this.rootStore.editorStore.editor.commandAdjustKaraokeClipTime(
      element.source.id,
      { startSeconds: startOffset, endSeconds: endOffset },
    );
  }

  private async legacy_updateKaraokeElementPlacement(
    element: ElementState,
    placement: Pick<ElementState, 'duration' | 'globalTime' | 'trimStart'>,
  ) {
    if (element.track !== KARAOKE_TRACK_NUMBER) return;

    const originalTranscription =
      this.rootStore.transcriptionStore.getOriginalTranscription();
    const originalLanguage = originalTranscription?.language;
    const karaokeConfig = this.videoCreator.karaokeProducer.getKaraokeConfig();

    await this.videoCreator.syncToEditorAtomically(async () => {
      await this.videoCreator.videoTranscriptionProcessor.applyKaraokeElementPlacement(
        element,
        placement,
        !originalLanguage?.includes('en') &&
          karaokeConfig.language.includes('en'),
      );
      this.videoCreator.refreshKaraokeElements();
    });
    return;
  }

  private async updateMediaElementPlacement(
    element: ElementState,
    placement: Pick<ElementState, 'duration' | 'globalTime' | 'trimStart'>,
    fadeProducer: FadeProducer,
  ) {
    if (!this.rootStore.editorStore.editor) return;

    if (
      (element.source.trim_start || 0) !== placement.trimStart ||
      (!placement.trimStart &&
        element.duration < placement.duration &&
        element.globalTime !== placement.globalTime)
    ) {
      // Trim start
      this.rootStore.editorStore.editor.commandTrimElementStart(
        element.source.id,
        placement.globalTime,
      );
    } else if (element.globalTime !== placement.globalTime) {
      // Move element
      this.rootStore.editorStore.editor.commandShiftElement(
        element.source.id,
        placement.globalTime,
      );
    } else if (element.duration !== placement.duration) {
      // Trim end
      this.rootStore.editorStore.editor.commandTrimElementEnd(
        element.source.id,
        placement.duration + placement.globalTime,
      );
    }

    // Handle stacked layout elements with headless commands
    const layoutElement =
      this.videoCreator.stackedLayoutManager.getLayoutElementFor(element);
    if (layoutElement) {
      const layoutModifications =
        this.videoCreator.stackedLayoutManager.extendModificationsToAllElements(
          layoutElement,
          placement,
        );

      if (layoutModifications) {
        // Apply each modification as individual headless commands
        for (const [key, value] of Object.entries(layoutModifications)) {
          if (key.includes('.time')) {
            const elementId = key.split('.')[0];
            this.rootStore.editorStore.editor.commandShiftElement(
              elementId,
              value as number,
            );
          } else if (key.includes('.duration')) {
            const elementId = key.split('.')[0];
            const element =
              this.rootStore.editorStore.editor.getElementById(elementId);
            if (element) {
              this.rootStore.editorStore.editor.commandTrimElementEnd(
                elementId,
                (value as number) + element.time,
              );
            }
          } else {
            // For other properties, use the general update command
            const [elementId, property] = key.split('.');
            this.rootStore.editorStore.editor.commandUpdateElementProperty(
              elementId,
              property,
              value,
            );
          }
        }
      }
    }

    if (element.source.type !== 'audio') {
      const newVideoOverlays = await fadeProducer.resetCrossfadeOnVideo(
        placement.globalTime,
        placement.duration,
      );
      if (Object.keys(newVideoOverlays).length > 0) {
        this.rootStore.videoSourceStore.applyModifications({
          ...newVideoOverlays,
        });
      }
    }
  }

  private async legacy_updateMainVideoElementPlacementAndTranscript(
    element: ElementState,
    placement: Pick<ElementState, 'duration' | 'globalTime' | 'trimStart'>,
    fadeProducer: FadeProducer,
  ) {
    // For main video element, we only allow moving and extending duration
    let addBreaksForRange;
    if ((element.source.trim_start || 0) !== (placement.trimStart || 0)) {
      if (!this.rootStore.editorStore.useTranscriptSegments) {
        addBreaksForRange =
          await this.videoCreator.videoTranscriptionProcessor.trimTrackStart(
            element.source.id,
            placement.globalTime,
            placement.trimStart,
            placement.duration,
            !this.rootStore.editorStore.headlessEnabled, // Don't edit source if Headless
          );
      } else {
        console.log('enableTranscriptSegmentArchitecture: trimStart');
      }

      if (this.rootStore.editorStore.headlessEnabled) {
        this.rootStore.editorStore.editor.commandTrimElementStart(
          element.source.id,
          placement.globalTime,
        );
      }
    } else if (element.globalTime !== placement.globalTime) {
      if (!this.rootStore.editorStore.useTranscriptSegments) {
        await this.videoCreator.videoTranscriptionProcessor.moveTrack(
          element.source.id,
          placement.globalTime,
          !this.rootStore.editorStore.headlessEnabled, // Don't edit source if Headless
        );
      } else {
        console.log('enableTranscriptSegmentArchitecture: moveTrack');
      }

      if (this.rootStore.editorStore.headlessEnabled) {
        this.rootStore.editorStore.editor.commandShiftElement(
          element.source.id,
          placement.globalTime,
        );
      }
    } else if (element.duration !== placement.duration) {
      if (!this.rootStore.editorStore.useTranscriptSegments) {
        addBreaksForRange =
          await this.videoCreator.videoTranscriptionProcessor.trimTrackDuration(
            element.source.id,
            placement.duration,
            !this.rootStore.editorStore.headlessEnabled, // Don't edit source if Headless
          );
      } else {
        console.log('enableTranscriptSegmentArchitecture: trimEnd');
      }

      if (this.rootStore.editorStore.headlessEnabled) {
        this.rootStore.editorStore.editor.commandTrimElementEnd(
          element.source.id,
          placement.duration + placement.globalTime,
        );
      }
    }

    const layoutElement =
      this.videoCreator.stackedLayoutManager.getLayoutElementFor(element);
    if (layoutElement) {
      const modifications =
        this.videoCreator.stackedLayoutManager.extendModificationsToAllElements(
          layoutElement,
          placement,
        );
      if (modifications) {
        await this.rootStore.videoSourceStore.applyModifications(modifications);
      }
    }

    await fadeProducer.tidyOriginalVideoOverlay();
    this.videoCreator.refreshKaraokeElements(addBreaksForRange);
    return;
  }

  private async legacy_updateMediaElementPlacement(
    element: ElementState,
    placement: Pick<ElementState, 'duration' | 'globalTime' | 'trimStart'>,
    fadeProducer: FadeProducer,
  ) {
    // Fallback to existing video source store modifications
    const trimStart = placement.trimStart || 0;
    const time = Math.max(placement.globalTime, 0);

    if (element.source.type === 'audio') {
      // Handle audio elements specifically
      if ((element.source.trim_start || 0) !== trimStart) {
        // Handle trim audio start
        await this.legacy_trimAudioTrackStart(
          element,
          time,
          trimStart,
          placement.duration,
        );
      } else if (element.globalTime !== time) {
        // Handle move track
        await this.rootStore.videoSourceStore.applyModifications({
          [`${element.source.id}.time`]: time,
          [`${element.source.id}.duration`]: element.source.duration,
        });
      } else if (element.duration !== placement.duration) {
        // Handle trim audio end
        await this.rootStore.videoSourceStore.applyModifications({
          [`${element.source.id}.time`]: time,
          [`${element.source.id}.duration`]: placement.duration,
        });
      }
    } else {
      // Handle other elements (image, text, etc.)
      const newVideoOverlays = await fadeProducer.resetCrossfadeOnVideo(
        placement.globalTime,
        placement.duration,
      );

      let modifications = {
        ...newVideoOverlays,
        [`${element.source.id}.time`]: placement.globalTime,
        [`${element.source.id}.duration`]: placement.duration,
        ...this.legacy_imageCompositionDurationModifications(
          element,
          placement.duration,
        ),
      };

      const layoutElement =
        this.videoCreator.stackedLayoutManager.getLayoutElementFor(element);
      if (layoutElement) {
        modifications = {
          ...modifications,
          ...this.videoCreator.stackedLayoutManager.extendModificationsToAllElements(
            layoutElement,
            placement,
          ),
        };
      }

      await this.rootStore.videoSourceStore.applyModifications(modifications);
    }
  }

  private async legacy_trimAudioTrackStart(
    element: ElementState,
    newStartTime: number,
    trimStart: number,
    newDuration: number,
  ) {
    const elementId = element.source.id;

    const stockSongs =
      this.videoCreator.stockMusic.flatMap(m => m.collection) || [];
    const ownSongs = this.videoCreator.story?.myAudios?.map(o => o.song) || [];
    const allSongs = [...stockSongs, ...ownSongs];

    let originalAudio = allSongs.find(c => c.url === element.source.source);

    let mediaDuration = element.mediaDuration!;
    let originalMediaDuration = parseFloat(originalAudio?.customData.duration!);

    const currentTrimStart = element.source.trim_start || 0;

    if (currentTrimStart < trimStart) {
      mediaDuration = Math.min(
        Math.max(mediaDuration - trimStart, 0),
        originalMediaDuration,
      );
    } else {
      // untrim
      const untrimedTime = currentTrimStart - trimStart;
      mediaDuration = Math.min(
        mediaDuration + untrimedTime,
        originalMediaDuration,
      );
    }

    // Trim the element
    await this.rootStore.videoSourceStore.applyModifications({
      [`${elementId}.time`]: newStartTime,
      [`${elementId}.trim_start`]: trimStart,
      [`${elementId}.duration`]: newDuration,
      [`${elementId}.media_duration`]: mediaDuration,
    });
  }

  private legacy_imageCompositionDurationModifications(
    element: ElementState,
    duration: number,
  ) {
    const additionalModifications: Record<string, any> = {};

    if (this.videoCreator.isImageElementComposition(element)) {
      for (let e of element.elements || []) {
        additionalModifications[`${e.source.id}.duration`] = duration;
      }
    }
    return additionalModifications;
  }
}
